import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const tips = [
  'Keep refill bags in your car so you\'re always prepared on the go.',
  'The scooper works great on grass, gravel, sand, and pavement.',
  'Rinse the scooper head with water after each use to keep it fresh.',
  'Stock up on refill bags — most dog owners go through 2–3 bags a month.',
];

export default function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || searchParams.get('checkout') || null;
  const [tipIndex, setTipIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setTipIndex((i) => (i + 1) % tips.length);
        setVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col">
      {/* Minimal nav */}
      <header className="w-full px-6 md:px-10 py-5 flex items-center justify-center border-b border-stone-100 bg-white">
        <a href="/">
          <img
            src="https://storage.readdy-site.link/project_files/1b4b5470-43ee-4e4c-a03f-80bf782ac886/295ea2cc-8770-43e6-ae89-4a5f4095a2e1_PAWZLY-7.png?v=947d967eac3d6db96af148317019d39e"
            alt="Pawzly Logo"
            className="h-16 w-auto object-contain"
          />
        </a>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16 md:py-24">
        <div className="w-full max-w-2xl mx-auto text-center">

          {/* Animated checkmark */}
          <div className="flex items-center justify-center mb-8">
            <div className="w-24 h-24 rounded-full bg-[#8A9B85]/15 flex items-center justify-center animate-[pulse_2s_ease-in-out_infinite]">
              <div className="w-16 h-16 rounded-full bg-[#8A9B85]/25 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-[#8A9B85] text-4xl"></i>
              </div>
            </div>
          </div>

          {/* Heading */}
          <h1
            className="text-4xl md:text-5xl font-bold text-stone-800 mb-4 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Order Confirmed!
          </h1>
          <p className="text-stone-500 text-lg mb-2">
            Thank you for your Pawzly order.
          </p>
          <p className="text-stone-400 text-sm mb-10">
            You'll receive a confirmation email shortly with your tracking details.
          </p>

          {orderId && (
            <div className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-5 py-2.5 mb-10">
              <i className="ri-receipt-line text-stone-400 text-sm"></i>
              <span className="text-stone-500 text-sm">Order ID: <strong className="text-stone-700">{orderId}</strong></span>
            </div>
          )}

          {/* What happens next */}
          <div className="bg-white rounded-3xl border border-stone-100 p-8 mb-10 text-left">
            <h2
              className="text-lg font-bold text-stone-800 mb-6 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              What happens next?
            </h2>
            <div className="flex flex-col gap-5">
              {[
                {
                  icon: 'ri-mail-check-line',
                  color: '#C4956A',
                  title: 'Confirmation Email',
                  desc: 'Check your inbox — your order summary is on its way.',
                },
                {
                  icon: 'ri-box-3-line',
                  color: '#8A9B85',
                  title: 'We Pack Your Order',
                  desc: 'Your scooper and refill bags are carefully packed within 1–2 business days.',
                },
                {
                  icon: 'ri-truck-line',
                  color: '#C4956A',
                  title: 'Shipped to Your Door',
                  desc: 'Standard delivery takes 3–7 business days. Free shipping on orders over $50.',
                },
                {
                  icon: 'ri-heart-line',
                  color: '#8A9B85',
                  title: 'Enjoy Cleaner Walks',
                  desc: 'Your pup (and your hands) will thank you.',
                },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${step.color}18` }}
                  >
                    <i className={`${step.icon} text-base`} style={{ color: step.color }}></i>
                  </div>
                  <div>
                    <p className="text-stone-800 font-semibold text-sm">{step.title}</p>
                    <p className="text-stone-400 text-sm mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rotating tip */}
          <div className="bg-[#F4F7F4] border border-[#8A9B85]/20 rounded-2xl px-6 py-5 mb-10 min-h-[80px] flex items-center justify-center">
            <div
              className={`flex items-start gap-3 transition-opacity duration-400 ${visible ? 'opacity-100' : 'opacity-0'}`}
            >
              <i className="ri-lightbulb-line text-[#8A9B85] text-lg flex-shrink-0 mt-0.5"></i>
              <p className="text-stone-600 text-sm leading-relaxed text-left">
                <strong className="text-[#8A9B85]">Pro tip:</strong> {tips[tipIndex]}
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="w-full sm:w-auto bg-[#C4956A] hover:bg-[#b5845a] text-white font-bold px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-home-line"></i>
              Back to Home
            </Link>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); window.scrollTo(0, 0); }}
              className="w-full sm:w-auto bg-white hover:bg-stone-50 text-stone-700 font-semibold px-8 py-4 rounded-full border border-stone-200 transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-share-line"></i>
              Share with a Friend
            </a>
          </div>

          {/* Social proof nudge */}
          <p className="text-stone-400 text-xs mt-10">
            Loving Pawzly? Tag us on Instagram{' '}
            <a href="#" className="text-[#8A9B85] font-medium hover:underline">@pawzly</a>{' '}
            for a chance to be featured!
          </p>
        </div>
      </main>

      {/* Footer strip */}
      <footer className="bg-[#4A5545] text-white/50 text-xs text-center py-5 px-6">
        <p>© 2026 Pawzly. All rights reserved. &nbsp;·&nbsp;
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
        </p>
      </footer>
    </div>
  );
}
